import React from "react";
import { XMarkIcon, InformationCircleIcon, ExclamationTriangleIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon, MinusIcon } from "@heroicons/react/24/outline";
import { SENSOR_INFO, METRIC_INFO, USEFUL_SENSORS } from "../utils/constants";

/**
 * SensorDetailModal shows detailed information about all sensors
 * when a user wants to explore beyond the primary 6 sensors.
 */
export function SensorDetailModal({ isOpen, onClose, sensorFeatures, assetId }) {
  if (!isOpen) return null;

  // Sort sensors: critical first, then high, medium, low
  const importanceOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  const sortedSensors = USEFUL_SENSORS.sort((a, b) => {
    const aImportance = SENSOR_INFO[a]?.importance || "low";
    const bImportance = SENSOR_INFO[b]?.importance || "low";
    return importanceOrder[aImportance] - importanceOrder[bImportance];
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content sensor-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>All Sensor Data - Engine {assetId}</h2>
          <button className="modal-close" onClick={onClose}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="modal-body">
          {/* Legend */}
          <div className="sensor-legend">
            <h4>Understanding the Metrics</h4>
            <div className="legend-grid">
              {Object.entries(METRIC_INFO).map(([key, info]) => (
                <div key={key} className="legend-item">
                  <span className="legend-label">{info.name}</span>
                  <span className="legend-desc">{info.interpretation}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Importance guide */}
          <div className="importance-guide">
            <span className="importance-tag critical">Critical</span>
            <span className="importance-desc">Primary failure indicators</span>
            <span className="importance-tag high">High</span>
            <span className="importance-desc">Important for degradation</span>
            <span className="importance-tag medium">Medium</span>
            <span className="importance-desc">Supporting metrics</span>
          </div>

          {/* All sensors grid */}
          <div className="all-sensors-grid">
            {sortedSensors.map((sensorKey) => {
              const info = SENSOR_INFO[sensorKey];
              const features = sensorFeatures?.[sensorKey];

              if (!info) return null;

              return (
                <SensorCard
                  key={sensorKey}
                  sensorKey={sensorKey}
                  info={info}
                  features={features}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Individual sensor card with full details
 */
function SensorCard({ sensorKey, info, features }) {
  const getTrendIcon = (slope) => {
    if (!slope) return <MinusIcon className="h-3.5 w-3.5" />;
    if (slope < -0.01) return <ArrowTrendingDownIcon className="h-3.5 w-3.5 trend-down" />;
    if (slope > 0.01) return <ArrowTrendingUpIcon className="h-3.5 w-3.5 trend-up" />;
    return <MinusIcon className="h-3.5 w-3.5 trend-flat" />;
  };

  const getImportanceClass = (importance) => {
    return `importance-${importance || "low"}`;
  };

  return (
    <div className={`sensor-detail-card ${getImportanceClass(info.importance)}`}>
      <div className="sensor-detail-header">
        <div className="sensor-id-section">
          <span className="sensor-id">{sensorKey.replace("sensor_", "S")}</span>
          <span className="sensor-short">{info.shortName}</span>
        </div>
        <span className={`importance-badge ${info.importance}`}>
          {info.importance}
        </span>
      </div>

      <div className="sensor-detail-name">{info.name}</div>

      <div className="sensor-detail-desc">
        <InformationCircleIcon className="h-3 w-3" />
        <span>{info.description}</span>
      </div>

      <div className="sensor-significance">
        {info.importance === "critical" && <ExclamationTriangleIcon className="h-3 w-3" />}
        <span>{info.significance}</span>
      </div>

      {features ? (
        <div className="sensor-detail-values">
          <div className="value-row">
            <span className="value-label">Mean</span>
            <span className="value-data">
              {features.mean?.toFixed(2)} {info.unit}
            </span>
          </div>
          <div className="value-row">
            <span className="value-label">Std Dev</span>
            <span className="value-data">{features.std?.toFixed(4)}</span>
          </div>
          <div className="value-row slope-row">
            <span className="value-label">Slope</span>
            <span className="value-data">
              {getTrendIcon(features.slope)}
              {features.slope >= 0 ? "+" : ""}
              {features.slope?.toFixed(5)} {info.unit && `${info.unit}/cycle`}
            </span>
          </div>
          <div className="value-row">
            <span className="value-label">Range</span>
            <span className="value-data">
              {features.min_val?.toFixed(2)} - {features.max_val?.toFixed(2)}
            </span>
          </div>
        </div>
      ) : (
        <div className="sensor-no-data">
          <span>No data available</span>
        </div>
      )}
    </div>
  );
}

/**
 * Compact info tooltip for inline help
 */
export function InfoTooltip({ text }) {
  return (
    <span className="info-tooltip" title={text}>
      <InformationCircleIcon className="h-3 w-3" />
    </span>
  );
}
